import * as React from 'react';
import { SlidersHorizontal, ChevronUp, ChevronDown } from 'lucide-react';
import { Button, type ButtonProps } from './button.js';
import { Input } from './input.js';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './dropdown-menu.js';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip.js';

export interface ColumnPickerColumn {
  id: string;
  /** Column header label. */
  label: string;
  /** Label shown in the picker instead of `label`, e.g. for an icon-only column header. */
  toggleLabel?: string;
}

export interface ColumnPickerProps {
  /**
   * All columns the picker may show, in display order — including pinned ids to exclude.
   * Role/permission filtering of which columns are visible or selectable (e.g. cost or
   * supplier-detail columns gated by capability) is the CALLER's responsibility — this
   * shared `packages/ui` primitive has no access to permissions and cannot enforce it.
   */
  columns: ColumnPickerColumn[];
  /** Column ids always excluded from the toggle list (e.g. a structural leading select column). */
  pinnedIds?: string[];
  hiddenColumnIds: ReadonlySet<string>;
  onToggle: (id: string) => void;
  /** Move a column one step up in the order. */
  onMoveUp?: (id: string) => void;
  /** Move a column one step down in the order. */
  onMoveDown?: (id: string) => void;
  /** Translated label for the trigger button tooltip / aria-label. */
  label: string;
  /** Trigger button size, e.g. `'icon-sm'` to match a dense icon-only toolbar. Defaults to `'icon'`. */
  triggerSize?: ButtonProps['size'];
  /** Translated section header rendered inside the dropdown. */
  sectionLabel: string;
  /** Translated placeholder for the column search input. */
  searchPlaceholder: string;
  /** Translated message shown when no columns match the search. */
  noResults: string;
}

/**
 * Trigger button + dropdown that lets users show/hide and reorder `DataTable` columns.
 * Columns whose id is in `pinnedIds` are excluded from this menu — they are structural
 * (e.g. a leading selection checkbox column) and not user-toggleable.
 */
export function ColumnPicker({
  columns,
  pinnedIds = [],
  hiddenColumnIds,
  onToggle,
  onMoveUp,
  onMoveDown,
  label,
  sectionLabel,
  searchPlaceholder,
  noResults,
  triggerSize = 'icon',
}: ColumnPickerProps) {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');

  const togglableColumns = columns.filter((col) => !pinnedIds.includes(col.id));
  const visibleColumns = togglableColumns.filter((col) =>
    (col.toggleLabel ?? col.label).toLowerCase().includes(search.trim().toLowerCase()),
  );

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(o) => {
        setOpen(o);
        if (!o) setSearch('');
      }}
    >
      <TooltipProvider delayDuration={300}>
        <Tooltip>
          <TooltipTrigger asChild>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size={triggerSize}
                aria-label={label}
                data-testid="column-picker-trigger"
              >
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
          </TooltipTrigger>
          <TooltipContent>{label}</TooltipContent>
        </Tooltip>
      </TooltipProvider>
      <DropdownMenuContent align="end" className="min-w-[220px] overflow-hidden">
        <div className="px-2 pb-2 pt-1">
          <Input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={searchPlaceholder}
            aria-label={searchPlaceholder}
            onKeyDown={(e) => e.stopPropagation()}
            onPointerDown={(e) => e.stopPropagation()}
            data-testid="column-search-input"
          />
        </div>
        <DropdownMenuLabel className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
          {sectionLabel}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <div className="max-h-[340px] overflow-y-auto">
          {visibleColumns.length === 0 ? (
            <p className="px-4 py-2 text-sm text-muted-foreground">{noResults}</p>
          ) : (
            visibleColumns.map((col) => {
              const fullIdx = togglableColumns.indexOf(col);
              return (
                <DropdownMenuCheckboxItem
                  key={col.id}
                  checked={!hiddenColumnIds.has(col.id)}
                  onCheckedChange={() => onToggle(col.id)}
                  // Prevent the dropdown from closing when toggling a checkbox
                  onSelect={(e) => e.preventDefault()}
                  data-testid={`column-toggle-${col.id}`}
                >
                  <span className="flex-1 truncate">{col.toggleLabel ?? col.label}</span>
                  {(onMoveUp ?? onMoveDown) ? (
                    <span className="flex items-center gap-0.5 ml-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={fullIdx === 0}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveUp?.(col.id);
                        }}
                        onSelect={(e: React.SyntheticEvent) => e.preventDefault()}
                        aria-label={`move-up-${col.id}`}
                        data-testid={`column-move-up-${col.id}`}
                      >
                        <ChevronUp className="h-3.5 w-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6"
                        disabled={fullIdx === togglableColumns.length - 1}
                        onClick={(e) => {
                          e.stopPropagation();
                          onMoveDown?.(col.id);
                        }}
                        onSelect={(e: React.SyntheticEvent) => e.preventDefault()}
                        aria-label={`move-down-${col.id}`}
                        data-testid={`column-move-down-${col.id}`}
                      >
                        <ChevronDown className="h-3.5 w-3.5" />
                      </Button>
                    </span>
                  ) : null}
                </DropdownMenuCheckboxItem>
              );
            })
          )}
        </div>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
